import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRide } from '../contexts/RideContext';
import { handleApiError } from '../lib/api';
import { MapPin, Navigation, Loader2, AlertCircle, Truck, Package, Utensils, Briefcase, ArrowRight, MessageSquare } from 'lucide-react';

const statusLabels: Record<string, string> = {
  requested: 'Demandée',
  searching: 'Recherche livreur',
  assigned: 'Assignée',
  accepted: 'Acceptée',
  en_route_pickup: 'En route vers le pickup',
  picked_up: 'Récupéré',
  en_route_delivery: 'En livraison',
  delivered: 'Livré',
  completed: 'Terminée',
  cancelled: 'Annulée',
  expired: 'Expirée',
};

const typeLabels: Record<string, string> = {
  delivery: 'Livraison',
  errand: 'Course',
  meal: 'Repas',
  package: 'Colis',
};

const typeIcons: Record<string, any> = {
  delivery: Package,
  errand: Navigation,
  meal: Utensils,
  package: Briefcase,
};

const statusColors: Record<string, string> = {
  requested: 'badge-gray',
  searching: 'badge-primary',
  assigned: 'badge-primary',
  accepted: 'badge-primary',
  en_route_pickup: 'badge-warning',
  picked_up: 'badge-warning',
  en_route_delivery: 'badge-warning',
  delivered: 'badge-success',
  completed: 'badge-success',
  cancelled: 'badge-error',
  expired: 'badge-gray',
};

export default function RideDetail() {
  const navigate = useNavigate();
  const { rideId } = useParams<{ rideId: string }>();
  const { user } = useAuth();
  const { currentRide, getRide, acceptRide, declineRide, updateRideStatus } = useRide();
  const [ride, setRide] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'details' | 'chat'>('details');

  useEffect(() => {
    if (!rideId) return;
    
    const loadRide = async () => {
      setLoading(true);
      setError('');
      try {
        const data = currentRide && currentRide.id === rideId ? currentRide : await getRide(rideId!);
        setRide(data);
      } catch (err) {
        setError(handleApiError(err));
      } finally {
        setLoading(false);
      }
    };
    
    loadRide();
  }, [rideId, currentRide, getRide]);

  const handleAccept = async () => {
    if (!ride || !user) return;
    setError('');
    try {
      await acceptRide(ride.id);
      navigate(`/ride/${ride.id}`);
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const handleDecline = () => {
    if (!ride) return;
    declineRide(ride.id);
    navigate('/available');
  };

  const handleStatusUpdate = async (status: string) => {
    if (!ride) return;
    try {
      await updateRideStatus(ride.id, { status: status as any });
      const updated = await getRide(ride.id);
      setRide(updated);
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 size={48} className="text-primary animate-spin" />
      </div>
    );
  }

  if (error || !ride) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <div className="card text-center">
          <AlertCircle size={48} className="mx-auto text-error mb-4" />
          <h2 className="text-xl font-semibold text-text mb-2">Course introuvable</h2>
          <p className="text-text-secondary mb-4">{error || 'Cette course n\'existe pas'}</p>
          <Link to="/" className="btn btn-primary">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  const TypeIcon = typeIcons[ride.type] || Package;
  const statusColor = statusColors[ride.status] || 'badge-gray';
  const canAccept = ride.status === 'searching' || ride.status === 'assigned';
  const canUpdate = ride.agentId === user?.id && 
    ['en_route_pickup', 'picked_up', 'en_route_delivery', 'delivered', 'completed', 'cancelled'].includes(ride.status);

  const nextStatuses: Record<string, string[]> = {
    en_route_pickup: ['picked_up', 'cancelled'],
    picked_up: ['en_route_delivery', 'cancelled'],
    en_route_delivery: ['delivered', 'cancelled'],
    delivered: ['completed', 'cancelled'],
  };

  const actionLabels: Record<string, string> = {
    picked_up: 'Colis récupéré',
    en_route_delivery: 'Démarrer la livraison',
    delivered: 'Confirmer la livraison',
    completed: 'Marquer terminée',
    cancelled: 'Annuler',
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container pt-4 pb-24">
        <div className="flex items-center justify-between mb-4">
          <Link to="/" className="btn btn-ghost" style={{padding: '8px'}}><ArrowRight size={24} /></Link>
          <h1 className="text-xl font-semibold text-text">Détails de la course</h1>
          <span className="w-10" />
        </div>

        <div className="card mb-4">
          <div className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <TypeIcon size={24} />
            </div>
            <div>
              <p className="font-medium text-text">{typeLabels[ride.type] || ride.type}</p>
              <p className="text-sm text-text-secondary">{ride.distanceKm?.toFixed(1)} km • {ride.estimatedDurationMin} min</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-2xl font-bold text-text">{ride.priceFcfa.toLocaleString()} FCFA</p>
              <p className="text-xs text-text-secondary">Gain: {ride.agentEarningsFcfa.toLocaleString()} FCFA</p>
            </div>
          </div>

          <div className="space-y-3 mt-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <MapPin size={20} className="text-primary" />
              <div>
                <p className="text-xs text-text-secondary">Départ</p>
                <p className="font-medium text-text">{ride.pickupAddress}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <MapPin size={20} className="text-secondary" />
              <div>
                <p className="text-xs text-text-secondary">Destination</p>
                <p className="font-medium text-text">{ride.dropoffAddress}</p>
              </div>
            </div>
            {ride.notes && (
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                <MessageSquare size={20} className="text-amber-600" />
                <div>
                  <p className="text-xs text-text-secondary">Note du client</p>
                  <p className="font-medium text-text">{ride.notes}</p>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-border pt-4 mt-4 flex items-center justify-between">
            <span className={`badge ${statusColor}`}>{statusLabels[ride.status] || ride.status}</span>
            <div className="text-right">
              <p className="text-xs text-text-secondary">Client: {ride.client?.fullName || 'N/A'}</p>
              {ride.client?.phone && <p className="text-xs text-text-secondary">{ride.client.phone}</p>}
            </div>
          </div>
        </div>

        <div className="flex gap-2 mb-4 border-b border-border">
          <button
            onClick={() => setActiveTab('details')}
            className={`flex-1 py-2 text-center font-medium ${activeTab === 'details' ? 'text-primary border-b-2 border-primary' : 'text-text-secondary'}`}
          >
            Détails
          </button>
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex-1 py-2 text-center font-medium ${activeTab === 'chat' ? 'text-primary border-b-2 border-primary' : 'text-text-secondary'}`}
          >
            <MessageSquare size={18} className="inline" /> Chat
          </button>
        </div>

        {activeTab === 'details' && (
          <div className="card space-y-4">
            {canAccept && (
              <div className="space-y-2">
                <button onClick={handleAccept} className="btn btn-primary btn-full text-lg py-4">
                  Accepter cette course <Truck size={20} />
                </button>
                <button onClick={handleDecline} className="btn btn-outline btn-full">
                  Refuser
                </button>
              </div>
            )}

            {canUpdate && nextStatuses[ride.status] && (
              <div>
                <p className="text-sm font-medium text-text mb-3">Changer le statut</p>
                <div className="grid grid-cols-2 gap-2">
                  {nextStatuses[ride.status].map(s => (
                    <button
                      key={s}
                      onClick={() => handleStatusUpdate(s)}
                      className={`btn ${s === 'cancelled' ? 'btn-danger' : 'btn-outline'}`}
                    >
                      {actionLabels[s] || statusLabels[s] || s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 pt-4 border-t border-border">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-text">{ride.agentEarningsFcfa.toLocaleString()}</p>
                <p className="text-xs text-text-secondary">Vos gains</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-text">{ride.priceFcfa.toLocaleString()}</p>
                <p className="text-xs text-text-secondary">Prix total</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'chat' && (
          <div className="card">
            <div className="space-y-4 mb-4 max-h-80 overflow-y-auto">
              <p className="text-center text-text-secondary">Chat non disponible pour le moment</p>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Messagerie bientôt disponible"
                className="input-field flex-1"
                disabled
              />
              <button className="btn btn-primary" disabled title="Messagerie bientôt disponible">
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}